import type { UserRole } from "./access-control";
import { APP_NAME_COPY, APP_ROUTE_COPY } from "./app-route-copy";
import { APP_ENVIRONMENTS, DASHBOARD_ENVIRONMENTS, type AppEnvironment } from "./feature-flags";

export const APP_NAME = APP_NAME_COPY;

const ALL_USER_ROLES = ["admin", "manager", "chemist"] as const satisfies readonly UserRole[];

export const APP_PATHS = {
  dashboard: "/dashboard",
  inventory: "/",
  transactions: "/transactions",
  contacts: "/contacts",
  orders: "/orders",
  audit: "/audit",
  users: "/users",
  auth: "/auth",
  updatePassword: "/update-password",
} as const;

import { API_PATHS } from "./api-endpoints";
export { API_PATHS };

export const contactPeoplePath = (contactId: number) => `${API_PATHS.contacts}/${contactId}/people`;
export const contactAddressesPath = (contactId: number) => `${API_PATHS.contacts}/${contactId}/addresses`;

export type AppRouteKey = keyof typeof APP_PATHS;

export const NAV_BADGES = {
  experimental: "experimental",
} as const;

type AppRouteMeta = {
  path: (typeof APP_PATHS)[AppRouteKey];
  navLabel?: string;
  navBadge?: string;
  pageTitle: string;
  pageDescription: string;
  visibleTo: readonly UserRole[];
  requiresAuth: boolean;
  environments: readonly AppEnvironment[];
};

export const APP_ROUTES: Record<AppRouteKey, AppRouteMeta> = {
  dashboard: {
    path: APP_PATHS.dashboard,
    navLabel: APP_ROUTE_COPY.dashboard.navLabel,
    pageTitle: APP_ROUTE_COPY.dashboard.navLabel,
    pageDescription: APP_ROUTE_COPY.dashboard.pageDescription,
    visibleTo: ALL_USER_ROLES,
    requiresAuth: true,
    environments: DASHBOARD_ENVIRONMENTS,
  },
  inventory: {
    path: APP_PATHS.inventory,
    navLabel: APP_ROUTE_COPY.inventory.navLabel,
    pageTitle: APP_NAME,
    pageDescription: APP_ROUTE_COPY.inventory.pageDescription,
    visibleTo: ALL_USER_ROLES,
    requiresAuth: true,
    environments: [APP_ENVIRONMENTS.development, APP_ENVIRONMENTS.trial, APP_ENVIRONMENTS.production],
  },
  transactions: {
    path: APP_PATHS.transactions,
    ...APP_ROUTE_COPY.transactions,
    visibleTo: ["admin", "manager"],
    requiresAuth: true,
    environments: [APP_ENVIRONMENTS.development, APP_ENVIRONMENTS.trial, APP_ENVIRONMENTS.production],
  },
  orders: {
    path: APP_PATHS.orders,
    navLabel: APP_ROUTE_COPY.orders.navLabel,
    pageTitle: APP_ROUTE_COPY.orders.navLabel,
    pageDescription: APP_ROUTE_COPY.orders.pageDescription,
    visibleTo: ALL_USER_ROLES,
    requiresAuth: true,
    environments: [APP_ENVIRONMENTS.development, APP_ENVIRONMENTS.trial, APP_ENVIRONMENTS.production],
  },
  contacts: {
    path: APP_PATHS.contacts,
    navLabel: APP_ROUTE_COPY.contacts.navLabel,
    pageTitle: APP_ROUTE_COPY.contacts.navLabel,
    pageDescription: APP_ROUTE_COPY.contacts.pageDescription,
    visibleTo: ["admin", "manager"],
    requiresAuth: true,
    environments: [APP_ENVIRONMENTS.development, APP_ENVIRONMENTS.trial, APP_ENVIRONMENTS.production],
  },
  audit: {
    path: APP_PATHS.audit,
    navLabel: APP_ROUTE_COPY.audit.navLabel,
    pageTitle: APP_ROUTE_COPY.audit.navLabel,
    pageDescription: APP_ROUTE_COPY.audit.pageDescription,
    visibleTo: ["admin", "manager"],
    requiresAuth: true,
    environments: [APP_ENVIRONMENTS.development, APP_ENVIRONMENTS.trial, APP_ENVIRONMENTS.production],
  },
  users: {
    path: APP_PATHS.users,
    navLabel: APP_ROUTE_COPY.users.navLabel,
    pageTitle: APP_ROUTE_COPY.users.navLabel,
    pageDescription: APP_ROUTE_COPY.users.pageDescription,
    visibleTo: ["admin"],
    requiresAuth: true,
    environments: [APP_ENVIRONMENTS.development, APP_ENVIRONMENTS.trial, APP_ENVIRONMENTS.production],
  },
  auth: {
    path: APP_PATHS.auth,
    ...APP_ROUTE_COPY.auth,
    visibleTo: ALL_USER_ROLES,
    requiresAuth: false,
    environments: [APP_ENVIRONMENTS.development, APP_ENVIRONMENTS.trial, APP_ENVIRONMENTS.production],
  },
  updatePassword: {
    path: APP_PATHS.updatePassword,
    ...APP_ROUTE_COPY.updatePassword,
    visibleTo: ALL_USER_ROLES,
    requiresAuth: false,
    environments: [APP_ENVIRONMENTS.development, APP_ENVIRONMENTS.trial, APP_ENVIRONMENTS.production],
  },
};

const APP_ROUTE_KEYS = Object.keys(APP_ROUTES) as AppRouteKey[];

export const NAV_ROUTE_KEYS: readonly AppRouteKey[] = APP_ROUTE_KEYS.filter(
  (key) => APP_ROUTES[key].navLabel !== undefined,
);

export function isRouteEnabledInEnvironment(
  key: AppRouteKey,
  environment: AppEnvironment,
): boolean {
  return APP_ROUTES[key].environments.includes(environment);
}

export function getNavRouteKeysForEnvironment(
  environment: AppEnvironment,
): readonly AppRouteKey[] {
  return NAV_ROUTE_KEYS.filter((key) => isRouteEnabledInEnvironment(key, environment));
}

export const PROTECTED_ROUTE_KEYS: readonly AppRouteKey[] = APP_ROUTE_KEYS.filter(
  (key) => APP_ROUTES[key].requiresAuth,
);

export function getAppRouteByPath(path: string): AppRouteMeta | null {
  const route = Object.values(APP_ROUTES).find(({ path: routePath }) => routePath === path);
  return route ?? null;
}
